import dayjs, { Dayjs } from 'dayjs';
import type { CenterCoachMetricKey } from '../../api/feedDashboard';
import type { CenterCoachRankingTrainer } from '../../services/useFeedDashboardQueryService';

export type CoachPerformanceMetricKey =
    | 'revenue'
    | 'totalClasses'
    | 'journalWriteRate'
    | 'newPayMembers'
    | 'reRegPayMembers'
    | 'reRegistrationRate'
    | 'reRegistrationSuccessRate'
    | 'closingRate';

export type CoachPerformanceMetricUnit = 'currency' | 'class' | 'member' | 'count' | 'percent';

/**
 * 「재등록 성공률」의 이름·정의·분자·분모 한 벌.
 *
 * 🔴 **화면마다 다른 말을 쓰면 같은 숫자에 두 가지 설명이 붙는다.** 서버가
 *    `centerCoachEvidence` 로 내려주는 문자열과 **같은 말**이어야 한다
 *    (백엔드 `DEFINITIONS.reRegistrationSuccessRate`).
 *
 * ⚠️ 지표 **키**(`reRegistrationSuccessRate`)와 근거 API 조회 키는 바꾸지 않는다.
 */
export const RE_REGISTRATION_SUCCESS_NAMES = {
    label: '재등록 성공률',
    definition: '그 달에 정상 종료한 회원 중 이후 재등록한 회원의 비율이에요.',
    numerator: '재등록 완료',
    denominator: '정상 종료',
} as const;

export type CoachPerformanceMetricDefinition = {
    key: CoachPerformanceMetricKey;
    label: string;
    unit: CoachPerformanceMetricUnit;
    evidenceKey: CenterCoachMetricKey | null;
};

/** 강사 앱과 동일한 순서로 노출하는 관리자 성과 요약 지표다. */
export const COACH_PERFORMANCE_METRICS: CoachPerformanceMetricDefinition[] = [
    { key: 'revenue', label: '강사 매출', unit: 'currency', evidenceKey: 'revenue' },
    { key: 'totalClasses', label: '수업 수', unit: 'class', evidenceKey: 'totalClasses' },
    { key: 'journalWriteRate', label: '일지 작성률', unit: 'percent', evidenceKey: 'journalWriteRate' },
    { key: 'newPayMembers', label: '신규등록 수', unit: 'count', evidenceKey: 'newPayMembers' },
    { key: 'reRegPayMembers', label: '재등록 수', unit: 'count', evidenceKey: 'reRegPayMembers' },
    { key: 'reRegistrationRate', label: '재등록률', unit: 'percent', evidenceKey: 'reRegistrationRate' },
    {
        key: 'reRegistrationSuccessRate',
        label: '재등록 성공률',
        unit: 'percent',
        evidenceKey: 'reRegistrationSuccessRate',
    },
    { key: 'closingRate', label: '신규기회 클로징률', unit: 'percent', evidenceKey: null },
];

export type CoachPerformanceUnavailableReason =
    | '집계 대상 없음'
    | '기능 미사용'
    | '불러오지 못함'
    | '집계 실패';

export type CoachPerformanceMetric = CoachPerformanceMetricDefinition & {
    value: number | null;
    rank: number | null;
    eligibleCoachCount: number | null;
    unavailableReason: CoachPerformanceUnavailableReason | null;

    /* ── 값 옆에 함께 적는 분자·분모 ──────────────────────────────
       `%` 만 보여주면 3명 중 1명과 300명 중 100명이 같은 카드가 된다.
       재등록 성공률만 채운다 (분자 = 재등록 완료 · 분모 = 정상 종료). */
    /** 비율의 분자 (재등록 성공률 = 재등록 완료 회원 수) */
    numerator?: number | null;
    /** 비율의 분모 (재등록 성공률 = 정상 종료 회원 수) */
    denominator?: number | null;
};

type StandardMetricKey = Exclude<CoachPerformanceMetricKey, 'closingRate'>;

export type RankableMetricRow = {
    coachId: string;
    value: number | null;
};

const DEGRADED_SOURCE_BY_METRIC: Partial<Record<StandardMetricKey, string>> = {
    revenue: 'sales',
    newPayMembers: 'sales',
    reRegPayMembers: 'sales',
    totalClasses: 'schedule',
    journalWriteRate: 'schedule',
    reRegistrationRate: 'reReg',
    reRegistrationSuccessRate: 'reRegSuccess',
};

const finiteNumberOrNull = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

const hasPositiveDenominator = (value: unknown) => {
    const denominator = finiteNumberOrNull(value);
    return denominator != null && denominator > 0;
};

/** 강사 앱(coachPerformance.service.toPercent)과 동일한 반올림. 소수 첫째 자리까지. */
const toPercent = (numerator: number, denominator: number): number | null =>
    denominator > 0 ? Math.round((numerator / denominator) * 1000) / 10 : null;

/**
 * 비율은 API가 0을 내려주더라도 분모가 없으면 null로 정규화한다.
 * 반대로 분모가 있고 값이 0이면 실제 0%이므로 그대로 보존한다.
 */
export const getStandardMetricValue = (
    trainer: CenterCoachRankingTrainer,
    key: StandardMetricKey,
): number | null => {
    switch (key) {
        case 'revenue':
        case 'totalClasses':
        case 'newPayMembers':
        case 'reRegPayMembers':
            return finiteNumberOrNull(trainer.periodFiltered?.[key]);
        case 'journalWriteRate': {
            // 강사 앱(fetchRawMetricRows)과 똑같이 breakdown 의 PT+그룹 합으로 재계산한다.
            // 상위 journalWriteRate 를 그대로 읽으면 그룹 수업이 반영된 앱 값과 어긋날 수 있다.
            const breakdown = trainer.periodFiltered?.journalWriteBreakdown;
            const hasBreakdown = breakdown?.pt != null || breakdown?.group != null;
            const denominator = hasBreakdown
                ? (breakdown?.pt?.target ?? 0) + (breakdown?.group?.target ?? 0)
                : trainer.periodFiltered?.journalWriteTarget ?? 0;
            const numerator = hasBreakdown
                ? (breakdown?.pt?.written ?? 0) + (breakdown?.group?.written ?? 0)
                : trainer.periodFiltered?.journalWriteWritten ?? 0;
            return toPercent(numerator, denominator);
        }
        case 'reRegistrationRate':
            return hasPositiveDenominator(trainer.snapshot?.activeMemberCount)
                ? finiteNumberOrNull(trainer.snapshot?.reRegistrationRate)
                : null;
        case 'reRegistrationSuccessRate':
            return hasPositiveDenominator(trainer.snapshot?.reRegistrationEndedMemberCount)
                ? finiteNumberOrNull(trainer.snapshot?.reRegistrationSuccessRate)
                : null;
    }
};

/**
 * 순위 한 벌 — 🔴 **값이 있는 행만 센다.**
 *
 * ─────────────────────────────────────────────────────────────
 * 같은 강사의 같은 지표가 화면마다 다른 등수로 보이면 안 된다
 * ─────────────────────────────────────────────────────────────
 * 이 함수는 **센터 통계의 「지표별 강사 순위」 탭과 강사 성과 요약 카드가 함께
 * 쓴다.** 예전에는 두 화면이 각자 셌고, 둘 다 값이 없는 행을 `?? 0` 으로
 * 뭉개 줄에 세웠다. 응답 순서가 `[미측정(null), 유효 0%]` 이면
 *   · 랭킹 탭  화면 순서대로 1위
 *   · 요약 카드 null 이 앞에 서서 2/2위
 * 로 **같은 강사가 다른 등수**로 보였다. 한 곳에서 세게 한다.
 *
 * 규칙
 *   - 값이 `null`(못 잼)인 행은 **줄에 세우지 않는다.** 0 으로 뭉개면
 *     '—' 가 찍힌 강사에게 금메달이 붙고, 정상 강사가 한 칸씩 밀린다.
 *   - 값 내림차순 안정정렬 후 index+1 → 동점자도 순차 등수(공동순위 아님)
 *   - 동점 tie-break 는 배열 순서(= center.relations 순서)
 *   - 분모는 **값이 있는 강사 수**다
 *
 * 강사 앱(`utils/coachPerformance.calculateCompetitionRank`)도 2026-09-14 에 같은
 * 규칙으로 맞췄다. 한쪽만 고치면 같은 강사가 두 화면에서 다른 분모를 본다 —
 * 바꿀 일이 생기면 **반드시 양쪽을 함께** 옮긴다.
 */
export const buildMeasuredRanking = (rows: RankableMetricRow[]): Map<string, number> => {
    const measured = rows
        .map((row, index) => ({ row, index }))
        .filter((entry): entry is { row: { coachId: string; value: number }; index: number } =>
            typeof entry.row.value === 'number' && Number.isFinite(entry.row.value),
        )
        .sort((a, b) => b.row.value - a.row.value || a.index - b.index);

    const ranking = new Map<string, number>();
    measured.forEach((entry, i) => ranking.set(entry.row.coachId, i + 1));
    return ranking;
};

/** 본인 값이 없으면 순위를 매기지 않고 분모도 노출하지 않는다. */
const calculateSequentialRank = (
    rows: RankableMetricRow[],
    coachId: string,
): { rank: number | null; eligibleCoachCount: number | null } => {
    const ranking = buildMeasuredRanking(rows);
    const rank = ranking.get(coachId);
    return rank == null
        ? { rank: null, eligibleCoachCount: null }
        : { rank, eligibleCoachCount: ranking.size };
};

export const calculateCompetitionRank = (
    trainers: CenterCoachRankingTrainer[],
    coachId: string,
    key: StandardMetricKey,
): { rank: number | null; eligibleCoachCount: number | null } =>
    calculateSequentialRank(
        trainers.map((trainer) => ({
            coachId: trainer.coachId,
            value: getStandardMetricValue(trainer, key),
        })),
        coachId,
    );

export const buildStandardCoachPerformanceMetrics = (
    trainers: CenterCoachRankingTrainer[],
    coachId: string,
    degradedSources: string[] = [],
): CoachPerformanceMetric[] => {
    const target = trainers.find((trainer) => trainer.coachId === coachId);

    return COACH_PERFORMANCE_METRICS.filter(
        (definition): definition is CoachPerformanceMetricDefinition & { key: StandardMetricKey } =>
            definition.key !== 'closingRate',
    ).map((definition) => {
        const degradedSource = DEGRADED_SOURCE_BY_METRIC[definition.key];
        if (degradedSource && degradedSources.includes(degradedSource)) {
            return {
                ...definition,
                value: null,
                rank: null,
                eligibleCoachCount: null,
                unavailableReason: '집계 실패' as const,
            };
        }
        const value = target ? getStandardMetricValue(target, definition.key) : null;
        const rank = calculateCompetitionRank(trainers, coachId, definition.key);
        return {
            ...definition,
            value,
            ...rank,
            unavailableReason: value == null ? '집계 대상 없음' : null,
            /* 값 옆에 「정상 종료 N명 중 M명」을 적기 위한 분자·분모 */
            ...(definition.key === 'reRegistrationSuccessRate'
                ? {
                      numerator: target?.snapshot?.reRegistrationSucceededMemberCount ?? null,
                      denominator: target?.snapshot?.reRegistrationEndedMemberCount ?? null,
                  }
                : {}),
        };
    });
};

export type NewOpportunitySummaryLike = {
    coachId?: string;
    closingRate?: number | null;
    leadMemberCount?: number | null;
    /** 백엔드가 'PERCENT'를 주면 0~100, 아니면 0~1 비율일 수 있다. */
    rateUnit?: string;
};

/**
 * 신규기회 요약 API의 비율은 rateUnit에 따라 0~1 비율로도 내려온다.
 * 신규기회 분석 화면(normalizeRatePercent)과 같은 규칙으로 백분율에 맞춘다.
 */
export const normalizeRatePercent = (value: unknown, rateUnit?: string): number | null => {
    const numeric = finiteNumberOrNull(value);
    if (numeric == null) return null;
    if (rateUnit === 'PERCENT') return numeric;
    return Math.abs(numeric) <= 1 ? numeric * 100 : numeric;
};

/** 클로징률 카드가 놓인 상황. featureOff/error 는 값 자체를 신뢰할 수 없다. */
export type ClosingRateState = 'ready' | 'featureOff' | 'error';

export const buildClosingRateMetric = (
    summary: NewOpportunitySummaryLike | undefined,
    state: ClosingRateState,
    rankInfo: Pick<CoachPerformanceMetric, 'rank' | 'eligibleCoachCount'> = {
        rank: null,
        eligibleCoachCount: null,
    },
): CoachPerformanceMetric => {
    const definition = COACH_PERFORMANCE_METRICS.find((metric) => metric.key === 'closingRate')!;
    const base = { ...definition, rank: null, eligibleCoachCount: null };
    if (state !== 'ready') {
        return {
            ...base,
            value: null,
            unavailableReason: state === 'featureOff' ? '기능 미사용' : '불러오지 못함',
        };
    }

    const value = hasPositiveDenominator(summary?.leadMemberCount)
        ? normalizeRatePercent(summary?.closingRate, summary?.rateUnit)
        : null;
    return {
        ...base,
        value,
        ...(value == null ? {} : rankInfo),
        unavailableReason: value == null ? '집계 대상 없음' : null,
    };
};

/** 강사앱과 동일하게 센터 강사 전원을 분모로 신규기회 클로징률 순위를 계산한다. */
export const buildClosingRateMetricForCoach = (
    trainers: CenterCoachRankingTrainer[],
    coachId: string,
    summaries: NewOpportunitySummaryLike[],
    state: ClosingRateState,
): CoachPerformanceMetric => {
    const summaryByCoach = new Map(
        summaries.filter((summary) => summary.coachId).map((summary) => [String(summary.coachId), summary]),
    );
    const valueOf = (summary: NewOpportunitySummaryLike | undefined) =>
        hasPositiveDenominator(summary?.leadMemberCount)
            ? normalizeRatePercent(summary?.closingRate, summary?.rateUnit)
            : null;
    const rankInfo = calculateSequentialRank(
        trainers.map((trainer) => ({
            coachId: trainer.coachId,
            value: valueOf(summaryByCoach.get(trainer.coachId)),
        })),
        coachId,
    );
    return buildClosingRateMetric(summaryByCoach.get(coachId), state, rankInfo);
};

const monthStart = (month: string | Date | Dayjs) => dayjs(month).startOf('month');

export const buildNewOpportunitySummaryParams = (month: string | Date | Dayjs, coachId?: string) => {
    const start = monthStart(month);
    return {
        startTime: start.toISOString(),
        endTime: start.add(1, 'month').toISOString(),
        ...(coachId ? { coachId } : {}),
        includeActiveMembers: 'true',
        // 센터 전체 순위에는 byCoach 만 필요하다. 회원 원천행 생성·전송은 생략한다.
        includeRows: 'false',
    };
};

export const buildNewOpportunityReportPath = (month: string | Date | Dayjs, coachId: string) => {
    const start = monthStart(month);
    const params = new URLSearchParams({
        startDate: start.format('YYYY-MM-DD'),
        endDate: start.endOf('month').format('YYYY-MM-DD'),
        coachId,
    });
    return `/centerReport/newOpportunities?${params.toString()}`;
};

/** 원천 근거는 월 단위로만 적재되며, 강사 순위 화면과 동일한 조회 한도를 쓴다. */
export const EVIDENCE_MAX_LOOKBACK_MONTHS = 24;

export const isEvidenceMonthAvailable = (month: string | Date | Dayjs, now: string | Date | Dayjs = dayjs()) => {
    const monthsAgo = monthStart(now).diff(monthStart(month), 'month');
    return monthsAgo >= 0 && monthsAgo <= EVIDENCE_MAX_LOOKBACK_MONTHS;
};

/** 카드를 눌렀을 때 열 화면. 'none' 이면 근거가 없으므로 클릭 대상으로 노출하지 않는다. */
export type CoachPerformanceCardAction = 'evidence' | 'newOpportunity' | 'none';

export const getCoachPerformanceCardAction = (
    metric: CoachPerformanceMetric,
    isEvidenceAvailable: boolean,
): CoachPerformanceCardAction => {
    if (metric.key === 'closingRate') {
        // 값이 0/없음이어도 신규기회 상세는 볼 가치가 있지만, 기능이 꺼졌거나 조회에 실패하면 보낼 곳이 없다.
        return metric.unavailableReason === '기능 미사용' || metric.unavailableReason === '불러오지 못함'
            ? 'none'
            : 'newOpportunity';
    }
    if (!metric.evidenceKey || metric.value == null || !isEvidenceAvailable) return 'none';
    return 'evidence';
};

export const formatCoachPerformanceMetricValue = (metric: CoachPerformanceMetric) => {
    if (metric.value == null) return metric.unavailableReason ?? '집계 대상 없음';
    switch (metric.unit) {
        case 'currency':
            return `${metric.value.toLocaleString('ko-KR')}원`;
        case 'class':
            return `${metric.value.toLocaleString('ko-KR')}회`;
        case 'member':
            return `${metric.value.toLocaleString('ko-KR')}명`;
        case 'count':
            return `${metric.value.toLocaleString('ko-KR')}건`;
        case 'percent':
            return `${metric.value.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;
    }
};
